// Wizard state → WizardInput. The one seam where the wizard's own shapes become the engine's
// contract (Track C, s490). Pure: no DOM, no clock, no storage.
//
// The two steps that collect data disagree about direction, deliberately:
//
//   step 2 (Stage Gate v5)  rows[]  NEWEST-FIRST — "start with now, work backwards"
//   step 3 (Deck v8)        vals[]  OLDEST-FIRST — the deck is read I…V, current last
//   contract                        OLDEST-FIRST — idx N−1 is "now" (D&R s485.d1); the whole
//                                   engine reads c[N-1] as the present
//
// So the reverse happens HERE, once, and nowhere else. Every downstream build note about index
// direction collapses into this file — that is the point of having it.
//
// Model А (D&R s484.d1): step 2 collects only a name and a START per stage. A stage's end is
// not user input — it IS the next stage's start. The last stage ends at "now", frozen at submit
// time by the caller (`nowY`), never read from the clock in here.
//
// Adjacent contracts this deliberately satisfies:
//   · `current` on the last stage only — the wizard hardcodes NOW onto the top row, which is
//     what makes exactly-one-current true by construction (Kirill + CD, 2026-08-05).
//   · RICH sliders `{values, touched}` — `touched` is a System-1 signal since the per-stage mask
//     landed (s443.d5 Phase 2), so the wizard must emit it rather than collapse untouched to 0.

import { LAYERS, BUTTON_GROUP_KEYS, CUSTOM_SENTINEL } from '../contracts.js';

/** step-3 button group ids, in BG1…BG4 order (Deck v8 `GROUPS`). */
export const GROUP_IDS = ['trigger', 'fuel', 'for', 'left'];

/** BG3 ("what were you living for") is the only group that accepts a free custom string. */
const CUSTOM_GROUP = 'for';

/**
 * "MM/YYYY" → decimal year. Already-decimal numbers pass through, so a caller holding parsed
 * dates (imports, fixtures) can skip the string form. Same convention as
 * `scripts/parse-extract.mjs` so the two producers cannot drift apart.
 * @returns {number|null} null when there is nothing parseable
 */
export function toDecimalYear(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim().toLowerCase();
  if (s === 'present' || s === 'now') return null; // "now" is nowY's job, not a parsed date
  const mmYY = s.match(/(\d{1,2})\/(\d{4})/);
  if (mmYY) return +mmYY[2] + (+mmYY[1] - 1) / 12;
  const y = s.match(/(\d{4})/);
  return y ? +y[1] : null;
}

/**
 * @typedef {Object} Step2State  step-2 (Stage Gate v5) — NEWEST-FIRST
 * @property {{ name: string, start: string|number, openStart?: boolean }[]} rows
 *
 * @typedef {Object} Step3State  step-3 (Deck v8) — OLDEST-FIRST, parallel arrays
 * @property {(number|null)[][]} vals   per stage: 7 values in LAYERS order, null = untouched
 * @property {Object[]} [grp]           per stage: { trigger, fuel, for, left } — string[] each
 * @property {string[]} [cust]          per stage: the free BG3 string ('' = none)
 * @property {{T1:string,T2:string,T3:string}[]} [text]
 */

/**
 * @param {Step2State} s2
 * @param {Step3State} s3
 * @param {{ nowY: number, allowUndated?: boolean }} opts
 *   nowY = decimal year frozen at submit (ends the current stage);
 *   allowUndated = opt out of the wizard's own "every row carries a date" guard, for the service
 *   producers that legitimately have none (see below).
 * @returns {import('../contracts.js').WizardInput}
 */
export function toWizardInput(s2, s3, { nowY, allowUndated = false } = {}) {
  const rows = (s2 && s2.rows) || [];
  const vals = (s3 && s3.vals) || [];
  if (rows.length !== vals.length) {
    // A silent misalignment would hand the engine one stage's sliders under another stage's
    // name and date — wrong, and invisible in the output. Fail loudly at the seam instead.
    throw new Error(`wizard state is misaligned: step 2 has ${rows.length} stages, step 3 has ${vals.length}`);
  }
  const N = rows.length;

  const oldestFirst = rows.slice().reverse(); // ← the reverse, D&R s485.d1
  const startY = oldestFirst.map((r) => toDecimalYear(r && r.start));

  // ⭐ A WIZARD ROW WITHOUT A DATE IS A LOUD ERROR, NOT A QUIET PASS (D&R s895.d2).
  //
  // Why it needs saying at all: once step 2 keeps its unfinished row in a SEPARATE slot
  // (`session.stageDraft`) rather than inside `rows`, an undated row can no longer be a draft
  // that slipped through — it can only be damage. And the damage is invisible downstream:
  // `viewFromPassport.js:61`/`:367` use `every`/`some`, so ONE null start silently strips the
  // year axis, the date range and every duration from the WHOLE document, while the row still
  // counts towards `stageCount` and still clears the three-stage gate (s461). Today only the
  // screen prevents that; this throw makes it a property of the seam.
  //
  // The escape is explicit and named, because `startY: null` stays legal for the service
  // producers: `scripts/parse-extract.mjs` emits it when a biography carries no dates at all,
  // and `contracts.js` accepts it by design. Those callers say so at the call site instead of
  // inheriting the wizard's guarantee by accident.
  //
  // `openStart` rows are exempt, and that is not a loophole: an open start is the DECLARED form
  // of "this stage has no beginning" (s511.d2), dormant here and reachable only from imports.
  // A missing start that says so is a shape; a missing start that says nothing is damage, and
  // only the second one is what this guard is about. The wizard emits neither.
  if (!allowUndated) {
    // Sorted by screen index, not by the order the reverse happened to produce: the message is
    // read by a person looking at the list top-down.
    const undated = startY.reduce((acc, y, i) => (
      y === null && !oldestFirst[i]?.openStart ? [...acc, N - 1 - i] : acc
    ), []).sort((a, b) => a - b);
    if (undated.length) {
      // The address is the SCREEN row (newest-first), like every other address the wizard hands
      // out (s515.d7) — the person is sent back to a row they can actually see.
      throw new Error(`wizard stage rows carry no date: screen row(s) ${undated.join(', ')}`);
    }
  }

  const stages = oldestFirst.map((r, i) => ({
    name: (r && r.name) || '',
    startY: startY[i],
    // model А: the end is the neighbour's start; the last stage ends at submit-time "now"
    endY: i < N - 1 ? startY[i + 1] : (nowY ?? null),
    current: i === N - 1,
    openStart: !!(r && r.openStart),
  }));

  const sliders = vals.map((row) => {
    const values = {};
    const touched = {};
    LAYERS.forEach((L, li) => {
      const v = row ? row[li] : null;
      values[L] = Number.isFinite(v) ? v : null;
      touched[L] = values[L] !== null;
    });
    return { values, touched };
  });

  const text = (s3 && s3.text)
    ? s3.text.map((t) => ({ T1: t?.T1 ?? '', T2: t?.T2 ?? '', T3: t?.T3 ?? '' }))
    : undefined;

  const buttons = (s3 && s3.grp)
    ? s3.grp.map((g, i) => {
      const row = {};
      BUTTON_GROUP_KEYS.forEach((key, gi) => {
        const id = GROUP_IDS[gi];
        const picked = Array.isArray(g?.[id]) ? g[id].slice() : [];
        const custom = id === CUSTOM_GROUP ? String(s3.cust?.[i] ?? '').trim() : '';
        // s510.d4a: the deck stores the SENTINEL in the selection array and the typed text in a
        // separate per-stage store (`S.cust[i]`), so the two must be joined here — nowhere else
        // knows about the sentinel. Until cc-30 we appended the text and left `__cust` in place,
        // and §II printed it: "for the work · __cust · for the version of me that stayed".
        // RESOLVE, don't append: the sentinel becomes the text, in the position the user picked
        // it. Empty text means the chip is on with nothing typed — then the selection is simply
        // dropped, because "__cust" is not an answer.
        const resolved = picked.map((o) => (o === CUSTOM_SENTINEL ? custom : o)).filter(Boolean);
        // A custom string with no sentinel in the array can only come from a non-deck producer
        // (a test, an import). Keep appending it there so those callers are unaffected.
        row[key] = custom && !picked.includes(CUSTOM_SENTINEL) ? [...resolved, custom] : resolved;
      });
      return row;
    })
    : undefined;

  return {
    wizard: {
      sliders,
      ...(text ? { text } : {}),
      stages,
      ...(buttons ? { buttons } : {}),
    },
  };
}
